import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router'

import type { SidebarIcon, SidebarLink } from '@/components/sidebar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { isItemActive } from './navigation'

interface CollapsibleNavMenuProps {
  label: string
  icon: SidebarIcon
  items: SidebarLink[]
  pathname: string
  /** Rows leave the app, so nothing here is ever the current page. */
  external?: boolean
}

export function CollapsibleNavMenu({
  label,
  icon: Icon,
  items,
  pathname,
  external,
}: CollapsibleNavMenuProps) {
  const groupActive = !external && items.some((item) => isItemActive(pathname, item))

  return (
    <Collapsible defaultOpen={groupActive} className="group/nav-menu">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton className={cn('cursor-pointer', groupActive && 'font-semibold')}>
            <Icon color={groupActive ? 'var(--color-ink-900)' : 'var(--color-ink-400)'} />
            <span className="truncate">{label}</span>
            <ChevronRight className="text-ink-400 ml-auto h-3 w-3 transition-transform duration-200 group-data-[collapsible=icon]:hidden group-data-[state=open]/nav-menu:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {items.map((item) => {
              const active = !external && isItemActive(pathname, item)
              return (
                <SidebarMenuSubItem key={item.title}>
                  <SidebarMenuSubButton asChild isActive={active} className={cn(active && 'font-semibold')}>
                    {external ? (
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span>{item.title}</span>
                      </a>
                    ) : (
                      <Link to={item.url}>
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span>{item.title}</span>
                      </Link>
                    )}
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              )
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
